import { Star, Trash2 } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { countryName } from "../../../shared/address.ts";
import type { Item, Vendor, VendorItem } from "../../../shared/db.gen.ts";
import { ADDRESS_KIND_LABELS, ago, days, money, options, qty, VENDOR_KIND_LABELS } from "../lib/format.ts";
import { can, mutate } from "../lib/mutate.ts";
import { setSearch } from "../lib/router.tsx";
import { useRow, useRows } from "../lib/sync.ts";
import { AddressHints, addressFieldDefs, addressOfRow, PasteAddress, phoneField } from "../ui/AddressEditor.tsx";
import { CreateDialog } from "../ui/CreateDialog.tsx";
import { Badge, Button, Select, Thumb } from "../ui/controls.tsx";
import { type Column, DataTable } from "../ui/DataTable.tsx";
import { ListPage } from "../ui/ListPage.tsx";
import { type FieldDef, Fields, RecordPanel, Section } from "../ui/RecordPanel.tsx";
import { PaneLink, usePaneOpen } from "../ui/SidePane.tsx";
import { VendorWise } from "./bills/VendorWise.tsx"; // Bills and payments ---
import { VendorClosures } from "./Closures.tsx";
import { itemPath } from "./ItemsPage.tsx";
import { VendorPos } from "./po/VendorPos.tsx";
import { ContactPortal, OpenPortalButton, VendorProducts } from "./VendorPortal.tsx"; // Vendor portal ---
import { VendorPrices, VendorUploads } from "./VendorPrices.tsx"; // Vendor portal --- prices and catalogs they sent

const kindTone = { supplier: "neutral", contract_mfr: "accent", both: "accent" } as const;

const columns: Column<Vendor>[] = [
  {
    key: "name",
    header: "Name",
    width: "minmax(240px,2fr)",
    cell: (v) => <span className={v.archived ? "text-faint line-through" : "font-medium"}>{v.name}</span>,
  },
  { key: "short_name", header: "Short name", width: "160px" },
  {
    key: "kind",
    header: "Type",
    width: "190px",
    value: (v) => VENDOR_KIND_LABELS[v.kind],
    cell: (v) => <Badge tone={kindTone[v.kind]}>{VENDOR_KIND_LABELS[v.kind]}</Badge>,
  },
  { key: "currency", header: "Currency", width: "90px" },
  { key: "country", header: "Country", width: "80px" },
  { key: "timezone", header: "Time zone", width: "150px" },
  { key: "updated_at", header: "Updated", width: "130px", cell: (v) => <span className="text-muted">{ago(v.updated_at)}</span> },
];

const fields: FieldDef[] = [
  { key: "name", label: "Name" },
  { key: "short_name", label: "Short name" },
  { key: "aliases", label: "Also known as", type: "tags", keepCase: true, placeholder: "Other names, e.g. Shopify's vendor field" },
  { key: "kind", label: "Type", type: "select", options: options(VENDOR_KIND_LABELS) },
  { key: "currency", label: "Currency", mono: true },
  { key: "payment_terms", label: "Payment terms", placeholder: "e.g. 30% deposit, 70% before shipment" },
  { key: "incoterms", label: "Incoterms", placeholder: "e.g. EXW, FOB Shenzhen" },
  { key: "website", label: "Website" },
  { key: "timezone", label: "Time zone", placeholder: "e.g. Asia/Shanghai" },
  // Vendor closures --- its country's closures (Chinese New Year for CN) apply to it
  { key: "country", label: "Country", mono: true, placeholder: "e.g. CN: its country's closures apply" },
  {
    key: "language",
    label: "Language",
    type: "select",
    options: [
      { value: "en", label: "English" },
      { value: "zh", label: "简体中文" },
    ],
  },
  // Forecasting --- defaults the purchasing plan uses for this vendor's links that have none
  { key: "lead_time_days", label: "Lead time", type: "number", placeholder: "Days, when a link has none", format: (v) => (v == null ? "" : `${v} days`) },
  { key: "moq", label: "MOQ", type: "number", placeholder: "Units per line, when a link has none", format: (v) => (v == null ? "" : qty(v)) },
  {
    key: "order_cycle_days",
    label: "Order cycle",
    type: "number",
    placeholder: "Days of stock each order buys",
    format: (v) => (v == null ? "" : `${v} days`),
  },
  { key: "notes", label: "Notes", type: "textarea" },
  { key: "archived", label: "Archived", type: "bool" },
];

export function VendorsPage() {
  const vendors = useRows("vendor");
  const [creating, setCreating] = useState(false);
  return (
    <>
      <ListPage
        title="Vendors"
        rows={vendors}
        columns={columns}
        newLabel="New vendor"
        onNew={can("vendor.create") ? () => setCreating(true) : undefined}
        pane="vendor"
      />
      {creating && (
        <CreateDialog
          title="New vendor"
          fields={[
            { key: "name", label: "Name", placeholder: "Legal or trading name" },
            { key: "short_name", label: "Short name", placeholder: "What the team calls them" },
            fields[3],
          ]}
          onClose={() => setCreating(false)}
          onSubmit={async (v) => {
            const id = crypto.randomUUID();
            const ok = await mutate("vendor.create", { id, name: v.name, short_name: v.short_name, kind: v.kind as Vendor["kind"] });
            if (ok) setSearch({ open: id });
            return ok;
          }}
        />
      )}
    </>
  );
}

export function VendorPanel({ id, close }: { id: string; close: () => void }) {
  const vendor = useRow("vendor", id);
  if (!vendor) return null;
  return (
    <RecordPanel
      title={vendor.short_name || vendor.name}
      paneTitle={false}
      subtitle={vendor.short_name ? vendor.name : undefined}
      badges={
        <>
          <Badge tone={kindTone[vendor.kind]}>{VENDOR_KIND_LABELS[vendor.kind]}</Badge>
          {vendor.archived && <Badge tone="warn">Archived</Badge>}
        </>
      }
      top={
        // Vendor portal --- into their portal, acting for them
        <div className="px-2 pb-1">
          <OpenPortalButton vendor={vendor} />
        </div>
      }
      row={vendor}
      fields={fields}
      update="vendor.update"
      entity="vendor"
      onClose={close}
    >
      <LikelyDuplicates vendor={vendor} />
      <Contacts vendor={vendor} />
      <Addresses vendorId={vendor.id} />
      <VendorClosures vendor={vendor} />
      <VendorPos vendorId={vendor.id} />
      <VendorWise vendorId={vendor.id} />
      <VendorPrices vendor={vendor} />
      <VendorProducts vendor={vendor} />
      <VendorUploads vendor={vendor} />
      <SuppliedItems vendorId={vendor.id} />
      <MergeVendor vendor={vendor} />
    </RecordPanel>
  );
}

function Card({ title, onRemove, children }: { title: ReactNode; onRemove?: () => void; children: ReactNode }) {
  return (
    <div className="mx-2 mb-2 rounded-lg border border-line">
      <div className="flex items-center gap-2 border-b border-line px-3 py-1.5">
        <div className="flex min-w-0 flex-1 items-center gap-2 truncate font-medium">{title}</div>
        {onRemove && (
          <button type="button" className="rounded p-1 text-faint hover:bg-hover hover:text-danger" title="Remove" onClick={onRemove}>
            <Trash2 className="size-3.5" />
          </button>
        )}
      </div>
      <div className="py-1">{children}</div>
    </div>
  );
}

const contactFields: FieldDef[] = [
  { key: "name", label: "Name" },
  { key: "role", label: "Role", placeholder: "e.g. Sales, Engineering" },
  { key: "email", label: "Email" },
  phoneField("phone"), // Addresses and phones --- shown as its country writes it, saved one way
  { key: "wechat", label: "WeChat" },
  { key: "is_po_recipient", label: "Gets POs", type: "bool" },
  // Vendor portal --- signs in at /v to keep their part numbers, photos and stock (needs their email)
  { key: "portal", label: "Vendor portal", type: "bool" },
  { key: "notes", label: "Notes", type: "textarea" },
];

function Contacts({ vendor }: { vendor: Vendor }) {
  const vendorId = vendor.id;
  const contacts = useRows("vendor_contact").filter((c) => c.vendor_id === vendorId);
  const editable = can("vendor_contact.update");
  // The first contact gets the POs unless someone changes it.
  const add = () => mutate("vendor_contact.create", { id: crypto.randomUUID(), vendor_id: vendorId, is_po_recipient: !contacts.length });
  return (
    <Section title="Contacts" count={contacts.length} addLabel="Add contact" onAdd={can("vendor_contact.create") ? add : undefined}>
      {!contacts.length && <div className="px-2 pb-2 text-muted">No contacts yet.</div>}
      {contacts.map((c) => (
        <Card
          key={c.id}
          title={
            <>
              {c.name || <span className="text-faint">New contact</span>}
              {c.is_po_recipient && <Badge tone="ok">Gets POs</Badge>}
              {c.portal && <Badge tone="accent">Portal</Badge>}
            </>
          }
          onRemove={
            can("vendor_contact.remove") ? () => confirm(`Remove ${c.name || "this contact"}?`) && mutate("vendor_contact.remove", { id: c.id }) : undefined
          }
        >
          <Fields compact row={c} fields={contactFields} update="vendor_contact.update" disabled={!editable} />
          {c.portal && <ContactPortal contact={c} vendor={vendor} editable={editable} />}
        </Card>
      ))}
    </Section>
  );
}

/** Addresses and phones --- an address's fields: its type and label, then its country's own fields (ui/AddressEditor.tsx). */
const addressFields = (country: string): FieldDef[] => [
  { key: "kind", label: "Type", type: "select", options: options(ADDRESS_KIND_LABELS) },
  { key: "label", label: "Label", placeholder: "e.g. Shenzhen factory" },
  ...addressFieldDefs(country),
  { key: "notes", label: "Notes", type: "textarea" },
];

function Addresses({ vendorId }: { vendorId: string }) {
  const addresses = useRows("vendor_address").filter((a) => a.vendor_id === vendorId);
  const vendor = useRow("vendor", vendorId);
  const editable = can("vendor_address.update");
  const add = () => mutate("vendor_address.create", { id: crypto.randomUUID(), vendor_id: vendorId, kind: addresses.length ? "ship_from" : "billing" });
  return (
    <Section title="Addresses" count={addresses.length} addLabel="Add address" onAdd={can("vendor_address.create") ? add : undefined}>
      {!addresses.length && <div className="px-2 pb-2 text-muted">No addresses yet.</div>}
      {addresses.map((a) => (
        <Card
          key={a.id}
          title={[ADDRESS_KIND_LABELS[a.kind], a.label || [a.city, a.country && countryName(a.country)].filter(Boolean).join(", ")].filter(Boolean).join(": ")}
          onRemove={can("vendor_address.remove") ? () => confirm("Remove this address?") && mutate("vendor_address.remove", { id: a.id }) : undefined}
        >
          <Fields compact row={a} fields={addressFields(a.country || vendor?.country || "")} update="vendor_address.update" disabled={!editable} />
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <AddressHints address={addressOfRow(a)} />
            </div>
            {editable && <PasteAddress hint={a.country || vendor?.country} onApply={(x) => mutate("vendor_address.update", { id: a.id, patch: x })} />}
          </div>
        </Card>
      ))}
    </Section>
  );
}

type Supplied = VendorItem & { item: Item };

/** The items this vendor supplies, in the usual table (views, columns, Copy / CSV); a row opens its item beside it. */
function SuppliedItems({ vendorId }: { vendorId: string }) {
  const links = useRows("vendor_item");
  const items = useRows("item");
  const paneOpen = usePaneOpen();
  const rows = useMemo(() => {
    const byId = new Map(items.map((i) => [i.id, i]));
    return links.flatMap((l): Supplied[] => {
      const item = l.vendor_id === vendorId ? byId.get(l.item_id) : undefined;
      return item ? [{ ...l, item }] : [];
    });
  }, [links, items, vendorId]);
  // Vendor portal --- their part number and the stock they say they have for us, when they changed it there
  const portalTitle = (l: Supplied) => (l.vendor_edited_at ? `Changed by them in the portal ${ago(l.vendor_edited_at)}` : undefined);
  const columns: Column<Supplied>[] = [
    {
      key: "sku",
      header: "SKU",
      width: "minmax(130px,1fr)",
      value: (l) => l.item.sku,
      cell: (l) => (
        <>
          <Thumb src={l.item.photo_url} size={20} />
          <PaneLink kind="item" id={l.item.id} to={itemPath(l.item)} className="truncate font-mono text-[12px] hover:underline">
            {l.item.sku}
          </PaneLink>
        </>
      ),
    },
    {
      key: "name",
      header: "Name",
      width: "minmax(160px,2fr)",
      value: (l) => l.item.name,
      cell: (l) => (
        <span className="truncate" title={l.item.name}>
          {l.item.name}
        </span>
      ),
    },
    {
      key: "vendor_sku",
      header: "Theirs",
      headerTitle: "Their part number",
      width: "minmax(90px,1fr)",
      cell: (l) => (
        <span className="truncate font-mono text-[12px] text-muted" title={portalTitle(l)}>
          {l.vendor_sku}
        </span>
      ),
    },
    {
      key: "vendor_stock",
      header: "Their stock",
      headerTitle: "The stock they say they have for us, in the vendor portal",
      width: "90px",
      align: "right",
      value: (l) => (l.vendor_stock == null ? null : Number(l.vendor_stock)),
      cell: (l) => (
        <span className="font-mono text-[12px] text-muted" title={portalTitle(l)}>
          {l.vendor_stock == null ? "" : qty(l.vendor_stock)}
        </span>
      ),
    },
    {
      key: "is_preferred",
      header: "Preferred",
      headerTitle: "This vendor is the item's preferred one",
      width: "84px",
      value: (l) => (l.is_preferred ? 0 : 1),
      text: (l) => (l.is_preferred ? "yes" : ""),
      cell: (l) => l.is_preferred && <Star className="size-3 shrink-0 text-ok" />,
    },
    {
      key: "unit_price",
      header: "Price",
      width: "90px",
      align: "right",
      value: (l) => (l.unit_price == null ? null : Number(l.unit_price)),
      cell: (l) => <span className="font-mono text-[12px] text-muted">{money(l.unit_price, l.currency)}</span>,
    },
    {
      key: "moq",
      header: "MOQ",
      width: "72px",
      align: "right",
      value: (l) => (l.moq ? Number(l.moq) : null),
      cell: (l) => <span className="font-mono text-[12px] text-muted">{l.moq ? qty(l.moq) : ""}</span>,
    },
    {
      key: "lead_time_days",
      header: "Lead time",
      width: "80px",
      align: "right",
      value: (l) => (l.lead_time_days == null ? null : Number(l.lead_time_days)),
      cell: (l) => <span className="font-mono text-[12px] text-muted">{days(l.lead_time_days)}</span>,
    },
  ];
  return (
    <Section title="Supplies" count={rows.length}>
      {!rows.length ? (
        <div className="px-2 pb-2 text-muted">Link this vendor to items from each item's panel.</div>
      ) : (
        <div className="px-2 pb-2">
          <DataTable
            fit
            rows={rows}
            columns={columns}
            onOpen={(l) => paneOpen("item", l.item.id)}
            rowNoun={["item", "items"]}
            exportName="Supplies"
            pageFilters={false}
          />
        </div>
      )}
    </Section>
  );
}

/** Like the server's vendor_key(): no case, punctuation or trailing Co./Ltd./Inc. */
const vendorKey = (name: string) =>
  name
    .toLowerCase()
    .replace(/[p{P}p{S}s]+/gu, " ")
    .trim()
    .replace(/( (co|company|corp|corporation|inc|incorporated|llc|ltd|limited|gmbh|plc|pte|pty|bv|srl))+$/, "");
const namesOf = (v: Vendor) => [v.name, v.short_name, ...v.aliases].filter(Boolean).map(vendorKey);

/** Other vendors whose names look like this one's (one starts with the other once Co./Ltd. are dropped). */
function useLikelyDuplicates(vendor: Vendor) {
  const vendors = useRows("vendor");
  return useMemo(() => {
    const mine = namesOf(vendor).filter((k) => k.length >= 4);
    return vendors.filter(
      (v) => v.id !== vendor.id && namesOf(v).some((k) => k.length >= 4 && mine.some((m) => m === k || m.startsWith(`${k} `) || k.startsWith(`${m} `))),
    );
  }, [vendors, vendor]);
}

async function merge(from: Vendor, into: Vendor) {
  if (
    !confirm(
      `Merge "${from.name}" into "${into.name}"? Its items, POs, contacts and addresses move to ${into.name}, its name is kept as another name for it, and "${from.name}" is deleted.`,
    )
  )
    return;
  if (await mutate("vendor.merge", { from_id: from.id, into_id: into.id })) setSearch({ open: into.id });
}

function LikelyDuplicates({ vendor }: { vendor: Vendor }) {
  const likely = useLikelyDuplicates(vendor);
  if (!likely.length || !can("vendor.merge")) return null;
  return (
    <div className="mx-2 mt-2 flex flex-col gap-1 rounded-lg border border-warn/40 bg-warn-soft px-3 py-2">
      {likely.map((v) => (
        <div key={v.id} className="flex items-center gap-2">
          <span className="min-w-0 flex-1">
            Looks like the same vendor as{" "}
            <PaneLink kind="vendor" id={v.id} to={`/vendors?open=${v.id}`} className="font-medium underline">
              {v.name}
            </PaneLink>
          </span>
          <Button size="sm" onClick={() => merge(vendor, v)}>
            Merge into it
          </Button>
        </div>
      ))}
    </div>
  );
}

function MergeVendor({ vendor }: { vendor: Vendor }) {
  const vendors = useRows("vendor");
  const [into, setInto] = useState("");
  const others = useMemo(
    () => vendors.filter((v) => v.id !== vendor.id).sort((a, b) => Number(a.archived) - Number(b.archived) || a.name.localeCompare(b.name)),
    [vendors, vendor.id],
  );
  if (!can("vendor.merge")) return null;
  const target = others.find((v) => v.id === into);
  return (
    <Section title="Duplicate?">
      <div className="flex items-center gap-2 px-2 pb-2">
        <Select
          className="min-w-0 flex-1"
          aria-label="Vendor to merge into"
          value={into}
          onChange={(e) => setInto(e.target.value)}
          options={[{ value: "", label: "Merge this vendor into…" }, ...others.map((v) => ({ value: v.id, label: v.name }))]}
        />
        <Button size="sm" variant="danger" disabled={!target} onClick={() => target && merge(vendor, target)}>
          Merge
        </Button>
      </div>
    </Section>
  );
}
